import { Select, Stack } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { AggFuncSubForm } from 'pages/revisions-module/template-editor/editors/form/configurator/properties/AggFuncSubForm';
import { useFormSections } from 'pages/revisions-module/template-editor/editors/form/section/FormSectionsDataProvider';
import {
  IFormInputSpec,
  IFormInputSpecDeviceFieldAggregator,
} from 'pages/revisions-module/template-editor/editors/form/types';
import { useEffect, useMemo } from 'react';

/**
 * Sub-form for device field aggregator input properties.
 */
export default function DeviceFieldAggregatorInputPropertiesSubForm({
  form,
}: {
  form: UseFormReturnType<IFormInputSpec>;
}) {
  const { sections } = useFormSections();

  const deviceSection = useMemo(() => sections.find(({ tab }) => tab === 'device'), [sections]);

  const fieldOptions = useMemo(
    () =>
      (deviceSection?.rows ?? [])
        .flatMap(({ inputs }) => inputs)
        .map(({ spec }) => ({ value: spec.name, label: spec.label })),
    [deviceSection]
  );

  const spec = form.values as IFormInputSpecDeviceFieldAggregator;

  const selectedFieldSpec = useMemo(
    () =>
      (deviceSection?.rows ?? [])
        .flatMap(({ inputs }) => inputs)
        .find(({ spec: inputSpec }) => inputSpec.name === spec.fieldName)?.spec,
    [deviceSection, spec.fieldName]
  );

  const selectedValueOptions = useMemo(
    () => (selectedFieldSpec?.type === 'select' ? selectedFieldSpec.options : undefined),
    [selectedFieldSpec]
  );

  useEffect(() => {
    if (spec.fieldName && !fieldOptions.some(({ value }) => value === spec.fieldName)) {
      form.setFieldValue('fieldName', fieldOptions[0]?.value || '');
    }
  }, [spec.fieldName, fieldOptions, form]);

  useEffect(() => {
    if (spec.aggFunc !== 'count-selected') {
      return;
    }

    if (selectedValueOptions && !selectedValueOptions.includes(spec.selectedValue || '')) {
      form.setFieldValue('selectedValue', selectedValueOptions[0] || '');
    }
  }, [spec.aggFunc, spec.selectedValue, selectedValueOptions, form]);

  return (
    <Stack spacing={12}>
      <Select
        required
        size="sm"
        label="Pole zariadenia"
        data={fieldOptions}
        nothingFound="Žiadne pole"
        disabled={fieldOptions.length === 0}
        {...form.getInputProps('fieldName')}
      />
      <AggFuncSubForm form={form} selectedValueOptions={selectedValueOptions} />
    </Stack>
  );
}
